import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import * as api from '../api.ts';
import { Directory, FullBleedList, InspectMuted, InspectOverlay } from '../directory.tsx';
import { TeamIcon } from '../icons.tsx';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  InlineAction,
  LoadError,
  Row,
  Select,
  Spinner,
  Table,
} from '../ui.tsx';

/**
 * Teams — its own rail directory: **a list of teams → team detail.**
 *
 * A Team is a named, org-scoped grouping that organises and filters people
 * (docs/teams.md) — a THIRD axis, separate from org role and from the
 * warden/ECO assignment. It is its own entity, so it gets its own rail item
 * rather than living as a section of People (the locked IA, 2026-09-07). The
 * second level — the team detail — lives inside a floating glass drawer over the
 * persistent map, never as a stacked rail column.
 *
 * v1 stays narrow on purpose (docs/teams.md): a team ORGANISES and FILTERS
 * people and is **not** an alert audience — nothing here touches activation.
 * What the detail carries: members (add/remove), the team's sites, a designated
 * manager (the team-scoped-admin seed), rename/delete, and a small settings
 * area. The People screen still shows and quick-assigns a person's teams; the
 * team entity itself is managed here.
 *
 * It reads on the shared rail-directory shell (`directory.tsx`) like the other
 * rail screens, with **no type column** — a team has no second axis to slice by,
 * so `Directory` renders just the sticky header (search + the Create-team CTA)
 * and the edge-to-edge list, rather than a bespoke `PageTitle` + boxed layout.
 * Create opens in the right `InspectOverlay` like People's Invite (F), never as
 * an inline form.
 */
export function Teams({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  // The Create-team CTA opens its form in the right inspect overlay (F), never
  // inline under the list.
  const [creating, setCreating] = useState(false);
  // The header search narrows the list; the whole content region scrolls under
  // the sticky header (People's model) rather than a boxed inner scroll.
  const [search, setSearch] = useState('');
  // The per-screen Site filter (owner decision 2026-09-08): the global rail
  // site switcher is gone, so a directory that is site-scoped carries its own.
  // A team spans zero or more buildings, so this narrows to the teams that
  // include the chosen one.
  const [site, setSite] = useState('');

  const teams = useQuery({ queryKey: ['teams', orgId], queryFn: () => api.getTeams(orgId) });
  // The People directory is the one source of who is in the org — the team
  // detail resolves member and manager labels from it rather than the teams
  // route carrying PII (docs/teams.md: the console never sees a user id, so the
  // teams route returns only ids).
  const people = useQuery({
    queryKey: ['people', orgId, {}],
    queryFn: () => api.getPeople(orgId, {}),
  });
  const sites = useQuery({ queryKey: ['sites', orgId], queryFn: () => api.getSites(orgId) });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['teams', orgId] });
    // A team change reshapes the People directory's team columns.
    void qc.invalidateQueries({ queryKey: ['people', orgId] });
  };

  const create = useMutation({
    mutationFn: (n: string) => api.createTeam(orgId, n),
    onSuccess: () => {
      setName('');
      setErr(null);
      setCreating(false);
      invalidate();
    },
    onError: (e: Error) => setErr(e.message),
  });

  const teamList = teams.data?.teams ?? [];
  const members = people.data?.members ?? [];
  const siteList = sites.data?.sites ?? [];

  // The detail reads the team from the live list, so a change reflects the
  // moment the query re-fetches; if the team is deleted, the drawer closes.
  const detailTeam = detailId ? (teamList.find((t) => t.id === detailId) ?? null) : null;
  useEffect(() => {
    if (detailId && !detailTeam && teams.isSuccess) setDetailId(null);
  }, [detailId, detailTeam, teams.isSuccess]);

  const q = search.trim().toLowerCase();
  const shown = teamList.filter(
    (t) =>
      (!q || t.name.toLowerCase().includes(q)) && (!site || t.sites.some((s) => s.id === site)),
  );
  const anyFilter = Boolean(q) || Boolean(site);
  const siteOptions = [
    { value: '', label: 'All sites' },
    ...siteList.map((s) => ({ value: s.id, label: s.name })),
  ];
  const loaded = teams.isSuccess;
  const meta = loaded ? (
    <>
      {teamList.length} {teamList.length === 1 ? 'team' : 'teams'}
      {anyFilter && shown.length !== teamList.length ? (
        <span className="text-[var(--ink-3)]">
          {' · '}
          {shown.length} of {teamList.length} shown
        </span>
      ) : null}
    </>
  ) : undefined;

  return (
    <Directory
      title="Teams"
      meta={meta}
      // Search + Create live in the sticky header; the list renders edge-to-edge
      // beneath it (no Section, no boxed scroll). Search is offered only once
      // there is a list to narrow.
      search={
        teamList.length > 0
          ? { value: search, onChange: setSearch, label: 'Search teams', placeholder: 'Team name' }
          : undefined
      }
      // The per-screen Site filter, in the header band — offered only once there
      // is a list to narrow AND more than one building to narrow by. `> 1`, not
      // `> 0`, deliberately matches Activity and the progressive-disclosure rule
      // ("don't show a café a structure it never grew into"): with a single
      // building the filter's only options are "All sites" + that one building,
      // which narrows nothing. A team can span zero-or-more sites, so one site is
      // arguably meaningful — but a lone-building filter still offers no real
      // choice, so it stays hidden until there are at least two to pick between.
      filters={
        teamList.length > 0 && siteList.length > 1 ? (
          <div className="w-40">
            <Select label="Site" value={site} onChange={setSite} options={siteOptions} />
          </div>
        ) : undefined
      }
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          Create team
        </Button>
      }
    >
      {teams.isError ? (
        <LoadError onRetry={() => void teams.refetch()}>
          Couldn’t load teams. This is a load error, not an empty list — retry.
        </LoadError>
      ) : teams.isLoading ? (
        <Spinner />
      ) : teamList.length === 0 ? (
        <Empty>No teams yet. Create one — the button is at the top of this page.</Empty>
      ) : shown.length === 0 ? (
        <Empty>
          No teams match this view. Clear the search and the site filter to see every team.
        </Empty>
      ) : (
        // Full-bleed under the Directory band, matching People and Locations so
        // the four rail lists read as one edge-to-edge surface (FullBleedList).
        <FullBleedList>
          <Table head={['Team', 'People', 'Sites', 'Manager', '']} stickyHeader>
            {shown.map((t) => (
              <TeamRow
                key={t.id}
                team={t}
                open={detailId === t.id}
                managerLabel={labelFor(members, t.managerUserId)}
                onOpen={() => setDetailId(t.id)}
              />
            ))}
          </Table>
        </FullBleedList>
      )}

      {/* Create a team — the CTA opens the form in the right inspect overlay (F),
          never an inline form under the list. */}
      {creating ? (
        <InspectOverlay
          ariaLabel="Create a team"
          title="Create a team"
          subtitle="Group people to organise and filter them"
          onClose={() => {
            setCreating(false);
            setErr(null);
          }}
        >
          <p className="mb-4 text-[13px] text-[var(--ink-2)]">
            A team is a group you name — Custodial, Court Registry, Night Shift — to organise and
            filter the people on your roster. A team can span several buildings, one, or none, and a
            person can be in more than one. It does not change who an alert reaches.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate(name.trim());
            }}
          >
            <div className="mb-4">
              <Field
                label="New team name"
                value={name}
                onChange={setName}
                placeholder="e.g. Custodial"
              />
            </div>
            <Button variant="primary" type="submit" disabled={!name.trim() || create.isPending}>
              Create team
            </Button>
          </form>
          {err ? (
            <div className="mt-4">
              <Finding tone="alert">{err}</Finding>
            </div>
          ) : null}
        </InspectOverlay>
      ) : null}

      {detailTeam ? (
        <TeamDetail
          orgId={orgId}
          team={detailTeam}
          members={members}
          sites={siteList}
          onClose={() => setDetailId(null)}
          onChanged={invalidate}
        />
      ) : null}
    </Directory>
  );
}

/** The label for a user id from the People directory, or null if none/unknown. */
function labelFor(members: api.PersonListEntry[], userId: string | null): string | null {
  if (!userId) return null;
  return members.find((m) => m.user_id === userId)?.label ?? null;
}

/** One team in the directory: the scannable columns and the way into the detail. */
function TeamRow({
  team,
  open,
  managerLabel,
  onOpen,
}: {
  team: api.Team;
  /** True while this team's inspect overlay is open — keeps the row highlighted. */
  open: boolean;
  managerLabel: string | null;
  onOpen: () => void;
}) {
  return (
    <Row active={open}>
      <Cell>
        <span className="flex items-center gap-2.5">
          {/* Leading team glyph in currentColor — the name carries the meaning. */}
          <span aria-hidden className="shrink-0 text-[var(--ink-2)]">
            <TeamIcon size={17} />
          </span>
          <button
            type="button"
            onClick={onOpen}
            aria-label={`Open and manage the ${team.name} team`}
            className="text-left text-[14px] text-[var(--accent)] underline-offset-2 hover:underline"
          >
            {team.name}
          </button>
        </span>
      </Cell>
      <Cell numeric>{team.memberCount}</Cell>
      <Cell wide>
        {team.sites.length > 0 ? (
          team.sites.map((s) => s.name).join(', ')
        ) : (
          <InspectMuted>—</InspectMuted>
        )}
      </Cell>
      <Cell wide>{managerLabel ?? <InspectMuted>None</InspectMuted>}</Cell>
      <Cell>
        <Button variant="quiet" label={`Open and manage the ${team.name} team`} onClick={onOpen}>
          Open
        </Button>
      </Cell>
    </Row>
  );
}

/**
 * The team detail — a slide-in glass panel over the map, and the full management
 * surface for one team: its members, its sites, its manager, and its settings
 * (rename, delete). A dialog rather than a second rail column, per the locked IA:
 * the second level lives inside the floating panel. It takes focus on open and
 * closes on Escape or a click outside.
 */
function TeamDetail({
  orgId,
  team,
  members,
  sites,
  onClose,
  onChanged,
}: {
  orgId: string;
  team: api.Team;
  members: api.PersonListEntry[];
  sites: api.Site[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [err, setErr] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(team.name);
  const [addUser, setAddUser] = useState('');
  const [addSite, setAddSite] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fail = (e: Error) => setErr(e.message);
  const ok = () => {
    setErr(null);
    onChanged();
  };

  const rename = useMutation({
    mutationFn: (n: string) => api.renameTeam(orgId, team.id, n),
    onSuccess: () => {
      setRenaming(false);
      ok();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => api.deleteTeam(orgId, team.id),
    onSuccess: () => {
      ok();
      onClose();
    },
    onError: fail,
  });
  const addMember = useMutation({
    mutationFn: (userId: string) => api.addTeamMember(orgId, team.id, userId),
    onSuccess: () => {
      setAddUser('');
      ok();
    },
    onError: fail,
  });
  const dropMember = useMutation({
    mutationFn: (userId: string) => api.removeTeamMember(orgId, team.id, userId),
    onSuccess: ok,
    onError: fail,
  });
  const addSiteM = useMutation({
    mutationFn: (siteId: string) => api.addTeamSite(orgId, team.id, siteId),
    onSuccess: () => {
      setAddSite('');
      ok();
    },
    onError: fail,
  });
  const dropSite = useMutation({
    mutationFn: (siteId: string) => api.removeTeamSite(orgId, team.id, siteId),
    onSuccess: ok,
    onError: fail,
  });
  const setManager = useMutation({
    mutationFn: (userId: string) => api.setTeamManager(orgId, team.id, userId),
    onSuccess: ok,
    onError: fail,
  });
  const clearManager = useMutation({
    mutationFn: () => api.clearTeamManager(orgId, team.id),
    onSuccess: ok,
    onError: fail,
  });

  // Members are resolved from the People directory (the teams route carries only
  // ids). A member is in this team when their team list includes it.
  const active = members.filter((m) => m.deprovisioned_at === null);
  const inTeam = active.filter((m) => m.teams.some((t) => t.id === team.id));
  const addable = active.filter((m) => !m.teams.some((t) => t.id === team.id));
  const teamSiteIds = new Set(team.sites.map((s) => s.id));
  const addableSites = sites.filter((s) => !teamSiteIds.has(s.id));
  const managerLabel = labelFor(members, team.managerUserId);

  // The right inspect overlay carries the dialog contract (glass frost backdrop,
  // focus trap, Esc, ✕, click-away, restore focus) through the shared shell — the
  // same overlay People/Locations/Activity use, so there is one drawer, not a
  // hand-rolled twin with its own grey scrim. This supplies the body only.
  return (
    <InspectOverlay
      ariaLabel={`Manage the ${team.name} team`}
      title={team.name}
      subtitle={`${team.memberCount} ${team.memberCount === 1 ? 'person' : 'people'}${
        team.sites.length > 0 ? ` · ${team.sites.length} site(s)` : ''
      }`}
      onClose={onClose}
    >
      {err ? (
        <div className="mb-4">
          <Finding tone="alert">{err}</Finding>
        </div>
      ) : null}

      {/* People ---------------------------------------------------------- */}
      <section className="mb-6">
        <h3 className="mb-2 text-[13px] font-[650] tracking-[0.04em] text-[var(--ink-2)] uppercase">
          People
        </h3>
        {inTeam.length === 0 ? (
          <p className="mb-2 text-[13px] text-[var(--ink-3)]">Nobody in this team yet.</p>
        ) : (
          <ul className="mb-3 flex flex-col gap-1">
            {inTeam.map((m) => (
              <li key={m.user_id} className="flex items-center justify-between gap-2 text-[14px]">
                <span className="text-[var(--ink)]">
                  {m.label}
                  {team.managerUserId === m.user_id ? (
                    <span className="ml-2 rounded-[var(--radius-1)] bg-[var(--paper-3)] px-1.5 py-0.5 text-[11px] text-[var(--ink-2)]">
                      Manager
                    </span>
                  ) : null}
                </span>
                <InlineAction
                  label={`Remove ${m.label} from ${team.name}`}
                  onClick={() => dropMember.mutate(m.user_id)}
                >
                  Remove
                </InlineAction>
              </li>
            ))}
          </ul>
        )}
        {addable.length > 0 ? (
          <div className="flex items-end gap-2">
            <div className="grow">
              <Select
                label="Add a person"
                value={addUser}
                onChange={setAddUser}
                options={[
                  { value: '', label: 'Choose someone…' },
                  ...addable.map((m) => ({ value: m.user_id, label: m.label })),
                ]}
              />
            </div>
            <Button
              variant="secondary"
              onClick={() => addUser && addMember.mutate(addUser)}
              disabled={!addUser || addMember.isPending}
            >
              Add
            </Button>
          </div>
        ) : (
          <p className="text-[13px] text-[var(--ink-3)]">Everyone on your list is in this team.</p>
        )}
      </section>

      {/* Manager --------------------------------------------------------- */}
      <section className="mb-6 border-t border-[var(--line)] pt-5">
        <h3 className="mb-2 text-[13px] font-[650] tracking-[0.04em] text-[var(--ink-2)] uppercase">
          Manager
        </h3>
        <p className="mb-3 text-[13px] text-[var(--ink-2)]">
          One member you nominate to run this team. It is recorded now as the team-scoped admin; the
          scoped permissions that follow from it are a later step. A manager must already be a
          member of the team.
        </p>
        {managerLabel ? (
          <div className="mb-3 flex items-center justify-between gap-2 text-[14px]">
            <span className="text-[var(--ink)]">{managerLabel}</span>
            <InlineAction
              label={`Remove ${managerLabel} as ${team.name}'s manager`}
              onClick={() => clearManager.mutate()}
            >
              Clear manager
            </InlineAction>
          </div>
        ) : (
          <p className="mb-3 text-[13px] text-[var(--ink-3)]">No manager designated.</p>
        )}
        {inTeam.length > 0 ? (
          <div className="w-full max-w-[280px]">
            <Select
              label={managerLabel ? 'Change manager' : 'Designate a manager'}
              value=""
              onChange={(v) => {
                if (v) setManager.mutate(v);
              }}
              options={[
                { value: '', label: 'Choose a member…' },
                ...inTeam
                  .filter((m) => m.user_id !== team.managerUserId)
                  .map((m) => ({ value: m.user_id, label: m.label })),
              ]}
            />
          </div>
        ) : (
          <p className="text-[13px] text-[var(--ink-3)]">
            Add people to the team before nominating a manager.
          </p>
        )}
      </section>

      {/* Settings (name + sites, room to grow) --------------------------- */}
      <section className="mb-6 border-t border-[var(--line)] pt-5">
        <h3 className="mb-3 text-[13px] font-[650] tracking-[0.04em] text-[var(--ink-2)] uppercase">
          Team settings
        </h3>

        <div className="mb-5">
          {renaming ? (
            <form
              className="flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (newName.trim()) rename.mutate(newName.trim());
              }}
            >
              <div className="w-56">
                <Field label="Team name" value={newName} onChange={setNewName} />
              </div>
              <Button
                variant="primary"
                type="submit"
                disabled={!newName.trim() || rename.isPending}
              >
                Save
              </Button>
              <Button variant="quiet" onClick={() => setRenaming(false)}>
                Cancel
              </Button>
            </form>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-[11px] font-medium tracking-[0.06em] text-[var(--ink-2)] uppercase">
                  Name
                </div>
                <div className="text-[14px] text-[var(--ink)]">{team.name}</div>
              </div>
              <InlineAction label={`Rename ${team.name}`} onClick={() => setRenaming(true)}>
                Rename
              </InlineAction>
            </div>
          )}
        </div>

        <div className="mb-5">
          <div className="mb-1.5 text-[11px] font-medium tracking-[0.06em] text-[var(--ink-2)] uppercase">
            Sites
          </div>
          {team.sites.length === 0 ? (
            <p className="mb-2 text-[13px] text-[var(--ink-3)]">No sites — this team spans none.</p>
          ) : (
            <ul className="mb-3 flex flex-col gap-1">
              {team.sites.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 text-[14px]">
                  <span className="text-[var(--ink)]">{s.name}</span>
                  <InlineAction
                    label={`Remove ${s.name} from ${team.name}`}
                    onClick={() => dropSite.mutate(s.id)}
                  >
                    Remove
                  </InlineAction>
                </li>
              ))}
            </ul>
          )}
          {addableSites.length > 0 ? (
            <div className="flex items-end gap-2">
              <div className="grow">
                <Select
                  label="Add a site"
                  value={addSite}
                  onChange={setAddSite}
                  options={[
                    { value: '', label: 'Choose a site…' },
                    ...addableSites.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                />
              </div>
              <Button
                variant="secondary"
                onClick={() => addSite && addSiteM.mutate(addSite)}
                disabled={!addSite || addSiteM.isPending}
              >
                Add
              </Button>
            </div>
          ) : (
            <p className="text-[13px] text-[var(--ink-3)]">Every site is already on this team.</p>
          )}
        </div>

        <div className="border-t border-[var(--line)] pt-4">
          {confirmDelete ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[14px]">
                Delete <strong className="font-medium">{team.name}</strong>? People and sites keep
                their records — only the grouping is removed.
              </span>
              <Button
                variant="primary"
                danger
                disabled={remove.isPending}
                label={`Confirm deleting the ${team.name} team`}
                onClick={() => remove.mutate()}
              >
                Confirm delete
              </Button>
              <Button variant="quiet" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="quiet"
              label={`Delete the ${team.name} team`}
              onClick={() => setConfirmDelete(true)}
            >
              Delete team
            </Button>
          )}
        </div>
      </section>
    </InspectOverlay>
  );
}
